
import { Link } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/lib/auth-store';
import {
  LayoutDashboard,
  ShoppingBag,
  Monitor,
  Users,
  UserPlus,
  Truck,
  Package,
  Tag,
  BarChart3,
  ScrollText,
  LogOut,
  ShieldCheck,
  Landmark,
  Printer,
  Building2,
} from 'lucide-react';

const NAV_SECTIONS = [
  {
    label: 'Main',
    items: [
      { href: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
      { href: '/orders', label: 'Orders', icon: ShoppingBag },
      { href: '/pos', label: 'POS Terminal', icon: Monitor },
      { href: '/customers', label: 'Customers', icon: Users },
    ],
  },
  {
    label: 'Operations',
    items: [
      { href: '/staff', label: 'Staff', icon: UserPlus },
      { href: '/drivers', label: 'Drivers', icon: Truck },
      { href: '/products', label: 'Products', icon: Package },
      { href: '/promotions', label: 'Promotions', icon: Tag },
      { href: '/reports', label: 'Reports', icon: BarChart3 },
      { href: '/release', label: 'Item Release', icon: ShieldCheck },
      { href: '/finora', label: 'Finora Sync', icon: Landmark },
    ],
  },
  {
    label: 'Settings',
    items: [
      { href: '/settings/hardware', label: 'Hardware', icon: Printer },
      { href: '/settings/bank-accounts', label: 'Bank Accounts', icon: Building2 },
    ],
  },
];

/** Quality control sees only Dashboard + Orders */
const QC_NAV_SECTIONS = [
  {
    label: 'Main',
    items: [
      { href: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
      { href: '/orders', label: 'Orders', icon: ShoppingBag },
      { href: '/release', label: 'Item Release', icon: ShieldCheck },
    ],
  },
];

export function AdminSidebar() {
  const pathname = useLocation().pathname;
  const { user, logout } = useAuthStore();

  return (
    <aside className="w-60 h-screen bg-white border-r border-gray-200 flex flex-col fixed left-0 top-0 z-30">
      <div className="px-5 py-4 border-b border-gray-200">
        <Link to="/dashboard" className="flex items-center">
          <img src="/logo.jpg" alt="CleanYarn" className="h-8 w-auto" />
        </Link>
      </div>

      <nav className="flex-1 px-3 py-4 overflow-y-auto">
        {(user?.role === 'quality_control' ? QC_NAV_SECTIONS : NAV_SECTIONS).map((section) => (
          <div key={section.label} className="mb-6">
            <p className="px-3 mb-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
              {section.label}
            </p>
            <div className="space-y-0.5">
              {section.items.map(({ href, label, icon: Icon }) => {
                const isActive = pathname === href || (href !== '/dashboard' && pathname.startsWith(href));
                return (
                  <Link
                    key={href}
                    to={href}
                    className={cn(
                      'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                      isActive
                        ? 'bg-accent-50 text-accent-700'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    )}
                  >
                    <Icon size={18} strokeWidth={isActive ? 2.5 : 2} />
                    {label}
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
        {user?.role === 'super_admin' && (
          <div className="mb-6">
            <p className="px-3 mb-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
              System
            </p>
            <div className="space-y-0.5">
              <Link
                to="/audit-logs"
                className={cn(
                  'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                  pathname.startsWith('/audit-logs')
                    ? 'bg-accent-50 text-accent-700'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                )}
              >
                <ScrollText size={18} strokeWidth={pathname.startsWith('/audit-logs') ? 2.5 : 2} />
                Audit Logs
              </Link>
            </div>
          </div>
        )}
      </nav>

      <div className="px-3 py-4 border-t border-gray-200">
        <div className="px-3 mb-3">
          <p className="text-sm font-medium text-gray-900 truncate">
            {user?.firstName} {user?.lastName}
          </p>
          <p className="text-xs text-gray-500 truncate capitalize">{user?.role?.replace(/_/g, ' ')}</p>
        </div>
        <button
          onClick={logout}
          className="flex items-center gap-3 w-full px-3 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-red-50 hover:text-red-600 transition-colors"
        >
          <LogOut size={18} />
          Sign Out
        </button>
      </div>
    </aside>
  );
}
